import { FC, useEffect, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import Axios, { CancelTokenSource } from 'axios';
import { withRouter, RouteComponentProps } from 'react-router-dom';
import { produce } from 'immer';

import { TileContainerExt } from '~src/modules/companySettings/shared.sc';
import { SectionHeader } from '~src/shared/form/shared.sc';
import LoadingSpinner from '~src/shared/loadingSpinner/LoadingSpinner';
import { routes } from '~src/routingConfig';
import {
  fetchImportsStatus,
  logError,
  ImportIntegration,
  fetchIntegrations,
  fetchImportStatus,
  ConfigSubmitObj,
} from '~src/services/fetch/import.service';
import { CompanySettingsLocationState } from '~src/modules/companySettings/types';

import { IntegrationTypes } from './types';
import IntegrationItem from './integrationItem/IntegrationItem';
import {
  IntegrationItemProps,
  UploadingStatusI,
  IntegrationStatus,
} from './integrationItem/integrationItem.types';
import { integrationsConfig } from './integrationsConfig';

export type IntegrationItemViewI = Omit<Partial<ImportIntegration>, 'name'> & {
  name: IntegrationTypes;
  props: IntegrationItemProps;
  importId?: string;
  uploadingStatus?: UploadingStatusI;
  auto_import: boolean;
};

const uploadingStatuses = ['pending', 'transforming', 'transformed', 'preparing'];

type Props = RouteComponentProps<
  Record<string, string>,
  Record<string, string>,
  CompanySettingsLocationState
>;

const IntegrationsList: FC<Props> = (props) => {
  const [t] = useTranslation();
  const [isLoadingIntegrations, setIsLoadingIntegrations] = useState(true);
  const importTimeout = useRef<number>();
  const importStatusToken = useRef<CancelTokenSource>();
  const [integrationItems, setIntegrationItems] = useState<IntegrationItemViewI[]>([]);

  useEffect(() => {
    void showIntegrations();
    return () => {
      clearTimeout(importTimeout.current);
      if (importStatusToken.current) {
        importStatusToken.current.cancel();
      }
    };
  }, []);

  const getIntegrations = async (): Promise<IntegrationItemViewI[] | undefined> => {
    try {
      const { data } = await fetchIntegrations();
      return integrationsConfig.map((itemView) => {
        const matchedItem = data.find(item => item.name === String(itemView.name));
        if (matchedItem) {
          return { ...matchedItem, ...itemView };
        }
        return itemView;
      });
    } catch (err) {
      logError('Error on getting integrations', '' + err, 'ImportSection.tsx');
      throw new Error('' + err);
    }
  };

  const showIntegrations = async () => {
    try {
      const [mappedIntegrations, importsStatusResp] = await Promise.all([
        getIntegrations(),
        fetchImportsStatus({ 'filter.running': true }),
      ]);
      let integrationsWithStatus: IntegrationItemViewI[] | undefined;

      if (importsStatusResp && mappedIntegrations) {
        const statusData = importsStatusResp.data;
        integrationsWithStatus = mappedIntegrations.map((intItem) => {
          if (intItem.id && (!props.location.state || !props.location.state.importCanceled)) {
            const statusItem = statusData.items.find(statusItm => statusItm.integration_id === intItem.id);
            if (statusItem) {
              if (uploadingStatuses.indexOf(statusItem.status) !== -1) {
                void getImportStatus(statusItem.id);
                return {
                  uploadingStatus: {
                    ...intItem.uploadingStatus,
                    status: IntegrationStatus.Running,
                    progress: statusItem.progress,
                  },
                  ...intItem,
                };
              } else if (statusItem.status === 'prepared') {
                return {
                  uploadingStatus: {
                    ...intItem.uploadingStatus,
                    status: IntegrationStatus.PreviewReady,
                  },
                  importId: statusItem.id,
                  ...intItem,
                };
              }
            }
          }
          return intItem;
        });
      }
      setIntegrationItems(integrationsWithStatus || mappedIntegrations || []);
    } finally {
      setIsLoadingIntegrations(false);
    }
  };

  const updateUploadingStatus = (integrationId: string, uploadingStatus: UploadingStatusI) => {
    setIntegrationItems(
      produce((draft: IntegrationItemViewI[]) => {
        const itemIndex = draft.findIndex(item => item.id === integrationId);
        if (itemIndex !== -1) {
          draft[itemIndex].uploadingStatus = {
            ...draft[itemIndex].uploadingStatus,
            ...uploadingStatus,
          };
        }
        return draft;
      }),
    );
  };

  const updateIntegrationState = (integrationId: string, submitObj: ConfigSubmitObj) => {
    setIntegrationItems(
      produce((draft: IntegrationItemViewI[]) => {
        const itemIndex = integrationItems.findIndex(item => item.id === integrationId);
        if (itemIndex !== -1) {
          draft[itemIndex].config = submitObj.config;
          draft[itemIndex].auto_import = submitObj.auto_import;
        }
        return draft;
      }),
    );
  };

  const getImportStatus = async (id: string, integrationId?: string) => {
    importStatusToken.current = Axios.CancelToken.source();
    if (integrationId) {
      updateUploadingStatus(integrationId, { status: IntegrationStatus.Running });
    }
    try {
      const { data } = await fetchImportStatus(id, {}, { cancelToken: importStatusToken.current.token });
      if (uploadingStatuses.indexOf(data.status) !== -1) {
        updateUploadingStatus(data.integration_id, {
          status: IntegrationStatus.Running,
          progress: data.stats.progress,
        });
        importTimeout.current = window.setTimeout(() => void getImportStatus(id), 1000);
      } else if (data.status === 'prepared') {
        updateUploadingStatus(data.integration_id, { status: IntegrationStatus.PreviewReady });
        goToImport(data.id);
      } else if (data.status === 'failed') {
        updateUploadingStatus(data.integration_id, {
          status: IntegrationStatus.Failed,
          error: `x.error.import.${data.error}`,
        });
      }
    } catch (err) {
      logError('Error on getting import status', '' + err, 'ImportSection.tsx');
    }
  };

  const goToImport = (importId: string) => {
    props.history.push(`${routes.company.importUsers}/${importId}`);
  };

  return (
    <TileContainerExt>
      <SectionHeader $inTile data-translation="x.company.import.title">
        {t('x.company.import.title')}
      </SectionHeader>
      {isLoadingIntegrations ? (
        <LoadingSpinner />
      ) : (
        integrationItems.map(item => (
          <IntegrationItem
            key={item.name}
            integration={item}
            getImportsStatus={getImportStatus}
            history={props.history}
            location={props.location}
            updateIntegrationState={updateIntegrationState}
            {...item.props}
          />
        ))
      )}
    </TileContainerExt>
  );
};

export default withRouter(IntegrationsList);
